/**
 * فیلترهای زمانیِ صفحه‌ی کشف: «کِی؟»
 *
 * دو تصمیمِ جدا که با هم «و» می‌شن:
 *   • کدوم روز — امروز، فردا، آخرِ هفته، یا یه تاریخِ دستی از تقویم.
 *   • کدام ساعتِ روز — صبح، بعدازظهر، شب. چندتایی، چون «صبح یا شب ولی نه
 *     ظهر» یه خواسته‌ی معمولیه.
 *
 * فقط به رخداد ربط داره: مکان تاریخ و ساعتِ کار توی داده‌ش نداره (نگاه کن به
 * `Business` توی core/domain/types.ts)، پس این فیلترها مثل «رایگان» فقط نوعِ
 * خودشون رو تنگ می‌کنن و مکان‌ها رو دست‌نخورده می‌ذارن.
 *
 * منطقِ خالص و بی‌وابستگی به React، مثلِ sort.ts — تا هم از صفحه در بیاد هم
 * بشه تستش کرد (نگاه کن به when.test.ts).
 */

/** کدوم روز. هم‌زمان فقط یکی — دو بازه‌ی روز با هم معنی نمی‌ده. */
export type WhenPreset = 'today' | 'tomorrow' | 'weekend' | 'date';

/** کدوم ساعتِ روز. چندتایی. */
export type DayPart = 'morning' | 'afternoon' | 'evening';

export const WHEN_PRESETS: WhenPreset[] = ['today', 'tomorrow', 'weekend', 'date'];
export const DAY_PARTS: DayPart[] = ['morning', 'afternoon', 'evening'];

/** کلیدِ ترجمه‌ی هر کدوم. */
export const WHEN_KEY: Record<WhenPreset, string> = {
  today: 'whenToday', tomorrow: 'whenTomorrow', weekend: 'whenWeekend', date: 'whenDate',
};
export const PART_KEY: Record<DayPart, string> = {
  morning: 'partMorning', afternoon: 'partAfternoon', evening: 'partEvening',
};

/**
 * مرزِ ساعتیِ هر بخشِ روز، به ساعتِ محلی: `[از، تا)`.
 *
 * شب از مرزِ نیمه‌شب رد می‌شه (۱۷ تا ۵ صبح)، برای همین `from > to` است و
 * تستِ داخل بودن باید «یا» باشه نه «و» — نگاه کن به `inPart`.
 */
const PART_HOURS: Record<DayPart, [number, number]> = {
  morning: [5, 12],
  afternoon: [12, 17],
  evening: [17, 5],
};

/** کمترین چیزی که برای فیلترِ زمانی لازمه — هم `EventItem` داردش هم نه‌بیشتر. */
export interface Timeable {
  startsAt?: string | null;
  endsAt?: string | null;
  /** رخدادِ تکرارشونده: ۰=شنبه … ۶=جمعه (نگاه کن به days() توی i18n.ts). */
  weekdays?: number[] | null;
  /** رخدادِ ماهانه: روزهای ماه، ۱..۳۱ (مایگریشن 0057). */
  monthdays?: number[] | null;
  /** هفته‌ی چندمِ ماه (۱..۵، ‎-1 = آخرین). فقط با `weekdays` معنی می‌ده. */
  monthWeeks?: number[] | null;
}

export interface WhenFilter {
  preset: WhenPreset | null;
  /** فقط وقتی `preset === 'date'` معنی داره. شکلش «YYYY-MM-DD»ِ محلیه. */
  date: string | null;
  parts: DayPart[];
}

export const EMPTY_WHEN: WhenFilter = { preset: null, date: null, parts: [] };

export const hasWhen = (w: WhenFilter): boolean => w.preset !== null || w.parts.length > 0;

/**
 * تاریخ به «YYYY-MM-DD»ِ **محلی**.
 *
 * عمداً `toISOString()` نیست: اون به UTC می‌بره و برای هر کسی که غربِ
 * گرینویچه، «امروز» یه روز عقب ذخیره می‌شد.
 */
export const isoDay = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** برعکسِ isoDay — نیمه‌شبِ همون روز به وقتِ محلی. */
export function dayFromIso(s: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

const midnight = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);

/** چند روز داره ماهی که این تاریخ توشه. */
const daysInMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();

const clean = (xs: number[] | null | undefined, lo: number, hi: number) =>
  (xs ?? []).filter((x) => Number.isInteger(x) && x >= lo && x <= hi);

/**
 * الگوی تکراری داره؟ یعنی «هیچ‌وقت نمی‌گذره» و «تاریخِ ثابتش تنها زمانش نیست».
 *
 * `monthWeeks`ِ تنها این‌جا حساب نمی‌شه: «اولینِ هر ماه» بدونِ اینکه بگی
 * اولینِ **چی**، هیچ روزی رو مشخص نمی‌کنه.
 */
export const isRecurring = (item: Timeable): boolean =>
  clean(item.weekdays, 0, 6).length > 0 || clean(item.monthdays, 1, 31).length > 0;

/**
 * این روزِ مشخص، روزِ اجرای این رخداده؟
 *
 * سه الگو که با «یا» جمع می‌شن:
 *   • روزِ ماه       — `monthdays` شاملِ شماره‌ی روز باشه.
 *   • هفتگی         — `weekdays` شاملِ روزِ هفته باشه و الگوی ماهانه‌ی
 *                     هفته‌محوری در کار نباشه.
 *   • n اُمین روزِ ماه — `weekdays` **و** `monthWeeks` با هم: «اولین جمعه».
 *
 * نکته‌ی ظریف: وقتی `monthWeeks` پره، `weekdays` دیگه «هر هفته» نیست بلکه
 * «کدوم روزِ هفته» است. اگه این‌طور نبود، «اولین جمعه‌ی هر ماه» با «هر جمعه»
 * یکی می‌شد و کلِ الگو بی‌اثر می‌موند.
 */
export function matchesDay(item: Timeable, d: Date): boolean {
  if (clean(item.monthdays, 1, 31).includes(d.getDate())) return true;

  const wd = clean(item.weekdays, 0, 6);
  // ۰=شنبه … ۶=جمعه؛ جاوااسکریپت از یکشنبه می‌شماره.
  if (!wd.includes((d.getDay() + 1) % 7)) return false;

  const wk = (item.monthWeeks ?? []).filter((w) => w === -1 || (w >= 1 && w <= 5));
  if (!wk.length) return true;

  const nth = Math.floor((d.getDate() - 1) / 7) + 1;
  // «آخرین» یعنی هفته‌ی بعد دیگه این روزِ هفته توی همین ماه نیست.
  const isLast = d.getDate() + 7 > daysInMonth(d);
  return wk.includes(nth) || (isLast && wk.includes(-1));
}

/**
 * بازه‌ی روزهای انتخاب‌شده: `[from, to)` — از نیمه‌شبِ روزِ اول تا نیمه‌شبِ
 * بعدِ روزِ آخر. `null` یعنی «هیچ روزی انتخاب نشده».
 *
 * «آخرِ هفته» شنبه و یکشنبه‌ست، و اگه همین الان وسطشی از امروز شروع می‌شه نه
 * از هفته‌ی بعد: کسی که شنبه ظهر «این آخر هفته» رو می‌زنه، امشب رو می‌خواد.
 */
export function dayRange(w: WhenFilter, now: Date = new Date()): { from: Date; to: Date } | null {
  switch (w.preset) {
    case 'today':
      return { from: midnight(now), to: addDays(now, 1) };
    case 'tomorrow':
      return { from: addDays(now, 1), to: addDays(now, 2) };
    case 'weekend': {
      const dow = now.getDay();   // ۰=یکشنبه … ۶=شنبه، به قرارِ جاوااسکریپت
      if (dow === 6) return { from: midnight(now), to: addDays(now, 2) };   // شنبه
      if (dow === 0) return { from: midnight(now), to: addDays(now, 1) };   // یکشنبه
      const untilSat = 6 - dow;
      return { from: addDays(now, untilSat), to: addDays(now, untilSat + 2) };
    }
    case 'date': {
      const d = w.date ? dayFromIso(w.date) : null;
      return d ? { from: d, to: addDays(d, 1) } : null;
    }
    default:
      return null;
  }
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * ساعت‌هایی که رخداد **اشغال** می‌کنه: `[از، تا)` به ساعتِ محلی. `null` یعنی
 * معلوم نیست.
 *
 * عمداً بازه‌ست نه یه ساعتِ شروع: رخدادی که چهار تا نُه طول می‌کشه هم بعدازظهره
 * هم شب، و رخدادِ تمامِ‌روز توی هر سه بخش هست. با «ساعتِ شروع»، جشنواره‌ای که
 * صبح باز می‌شه از فیلترِ «شب» می‌افتاد بیرون — با اینکه شب هم بازه.
 *
 * رخدادی که بیشتر از یه روز طول می‌کشه (یا از نیمه‌شب رد می‌شه) تمامِ روز
 * حساب می‌شه: توی روزهای وسطش واقعاً از صبح تا شب هست.
 */
function busyHours(item: Timeable): [number, number] | null {
  const startMs = item.startsAt ? new Date(item.startsAt).getTime() : NaN;
  if (Number.isNaN(startMs)) return null;
  const start = new Date(startMs);

  const endMs = item.endsAt ? new Date(item.endsAt).getTime() : NaN;
  // بی‌پایان (یا پایانِ خراب) = یه لحظه؛ یه ساعت براش باز می‌کنیم تا با
  // بخش‌های روز همپوشانی معنی بده.
  if (Number.isNaN(endMs) || endMs <= startMs) return [start.getHours(), start.getHours() + 1];

  const end = new Date(endMs);
  const sameDay = start.getFullYear() === end.getFullYear()
    && start.getMonth() === end.getMonth() && start.getDate() === end.getDate();
  if (!sameDay || endMs - startMs >= DAY_MS) return [0, 24];

  // پایانِ سرِ ساعتِ شروع (رخدادِ نیم‌ساعته) بازه‌ی صفر می‌داد؛ حداقل یه ساعت.
  return [start.getHours(), Math.max(end.getHours(), start.getHours() + 1)];
}

const overlaps = (a: number, b: number, from: number, to: number) => a < to && b > from;

/** این بازه با این بخشِ روز همپوشانی داره؟ شبی که از نیمه‌شب رد می‌شه هم درست. */
function inPart([a, b]: [number, number], part: DayPart): boolean {
  const [from, to] = PART_HOURS[part];
  return from <= to ? overlaps(a, b, from, to)
    : overlaps(a, b, from, 24) || overlaps(a, b, 0, to);
}

/**
 * روی این بازه می‌افته؟
 *
 * سه حالت، همون سه تایی که `nextOccurrence` هم می‌شناسه:
 *   • تکرارشونده: کافیه یکی از روزهای اجراش توی بازه باشه (هفتگی یا ماهانه).
 *   • یک‌باره: بازه‌ی خودش (`startsAt` تا `endsAt`) با این بازه همپوشانی داشته باشه.
 *   • بی‌تاریخ: هیچ‌وقت. رخدادی که نمی‌دونیم کِیه، جوابِ «امروز؟» نیست.
 */
function inRange(item: Timeable, from: Date, to: Date): boolean {
  if (isRecurring(item)) {
    for (let d = new Date(from); d < to; d = addDays(d, 1)) {
      if (matchesDay(item, d)) return true;
    }
    return false;
  }

  const startMs = item.startsAt ? new Date(item.startsAt).getTime() : NaN;
  if (Number.isNaN(startMs)) return false;
  const endMs = item.endsAt ? new Date(item.endsAt).getTime() : startMs;
  // همپوشانی، نه «شروعش توی بازه‌ست»: رخدادِ سه‌روزه‌ای که دیروز شروع شده
  // امروز هم هست.
  return startMs < to.getTime() && (Number.isNaN(endMs) ? startMs : endMs) >= from.getTime();
}

/**
 * این رخداد گذشته؟
 *
 * «گذشته» یعنی آخرین لحظه‌ش رد شده: پایانش اگه داره، وگرنه خودِ شروعش.
 *
 * دو حالت عمداً گذشته حساب **نمی‌شن**، همون دو تایی که `nextOccurrence` توی
 * sort.ts هم جدا می‌شناسه:
 *   • تکرارشونده (هفتگی یا ماهانه): دفعه‌ی بعد دوباره هست، پس هیچ‌وقت نمی‌گذره.
 *   • بی‌تاریخ: چیزی که نمی‌دونیم کِی بوده، نمی‌شه گفت تمام شده.
 *
 * جدا از `matchesWhen` است چون جوابِ سوالِ دیگه‌ایه: اون می‌گه «توی این بازه
 * هست؟» و این می‌گه «اصلاً هنوز هست؟» — فیلترهای «کِی؟» یه بازه‌ی انتخابی
 * می‌خوان و این هیچی نمی‌خواد جز خودِ الان.
 */
export function isExpired(item: Timeable, now: Date = new Date()): boolean {
  if (isRecurring(item)) return false;
  const times = [item.startsAt, item.endsAt]
    .map((iso) => (iso ? new Date(iso).getTime() : NaN))
    .filter((ms) => !Number.isNaN(ms));
  if (!times.length) return false;
  return Math.max(...times) < now.getTime();
}

/**
 * همون قاعده‌ی `isExpired`، این‌بار به زبانِ PostgREST — رشته‌ای که به
 * `.or(...)` داده می‌شه تا چیزی که تمام شده اصلاً از سرور دانلود نشه.
 *
 * چرا دو تا رندر از یک قاعده و نه یکی: `isExpired` باید روی چیزی که از کشِ
 * آفلاینِ گوشی در می‌آد هم کار کنه، جایی که هیچ سروری وسط نیست. پس هر دو
 * می‌مونن و عمداً کنارِ هم نوشته شدن تا با هم عوض شن؛ `discoverWhen.test.ts`
 * هم‌خوانی‌شون رو قفل می‌کنه.
 *
 * بندها همون سه حالتِ `isExpired` ان:
 *   • یکی از دو تاریخ هنوز نرسیده — یعنی `max(starts_at, ends_at) >= now`.
 *     همین یه شرط هر سه حالتِ داده رو می‌گیره: رخدادی که پایان داره، رخدادی
 *     که فقط شروع داره، و ردیفی که پایانش از شروعش عقب‌تره (هشت‌تا از این‌ها
 *     توی داده‌ی امروز هست؛ با شرطِ «پایان» تنها، بی‌دلیل حذف می‌شدن).
 *   • الگوی تکرار داره — دفعه‌ی بعد دوباره هست، پس هیچ‌وقت نمی‌گذره.
 *   • اصلاً تاریخ نداره — چیزی که نمی‌دونیم کِی بوده، تمام‌شده هم نیست.
 *
 * یه اختلافِ عمدی با `isRecurring`: آرایه‌ی **خالیِ** `weekdays` این‌جا
 * تکرارشونده حساب می‌شه، چون PostgREST طولِ آرایه رو نمی‌فهمه و فقط
 * «null هست یا نه» رو می‌تونه بپرسه. اختلاف به سمتِ «نگهش دار» است: ردیفِ
 * اضافه‌ای که کلاینت بعداً کنار می‌ذاره بی‌ضرره، ردیفِ گم‌شده نه.
 */
export function stillOnFilter(now: Date = new Date()): string {
  const iso = now.toISOString();
  return [
    `starts_at.gte.${iso}`,
    `ends_at.gte.${iso}`,
    'weekdays.not.is.null',
    'monthdays.not.is.null',
    'and(starts_at.is.null,ends_at.is.null)',
  ].join(',');
}

/**
 * این رخداد از فیلترِ زمانی رد می‌شه؟
 *
 * روز و ساعت با هم «و» می‌شن، ولی داخلِ خودِ ساعت‌ها «یا»: «صبح یا شب».
 */
export function matchesWhen(item: Timeable, w: WhenFilter, now: Date = new Date()): boolean {
  const range = dayRange(w, now);
  if (range && !inRange(item, range.from, range.to)) return false;

  if (w.parts.length) {
    const busy = busyHours(item);
    if (!busy) return false;
    if (!w.parts.some((p) => inPart(busy, p))) return false;
  }
  return true;
}
